import { useState, useEffect, useRef } from 'react'
import { Check, ChevronsUpDown, Edit2, Trash2, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  fetchStrainFamilies,
  createStrainFamily,
  updateStrainFamily,
  softDeleteStrainFamily,
  getProductCountByStrainFamily,
} from '../supabase-client'

export default function StrainFamilyComboBox({ value, onChange, disabled = false, error = false, onDataChange }) {
  const [open, setOpen] = useState(false)
  const [families, setFamilies] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [editingCode, setEditingCode] = useState('')
  const [originalName, setOriginalName] = useState('')
  const [originalCode, setOriginalCode] = useState('')
  const [hoveredId, setHoveredId] = useState(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [productCount, setProductCount] = useState(0)
  const [deleteId, setDeleteId] = useState(null)
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [newName, setNewName] = useState('')
  const [newCode, setNewCode] = useState('')
  const editNameRef = useRef(null)
  const newNameRef = useRef(null)

  useEffect(() => {
    loadFamilies()
  }, [])

  useEffect(() => {
    if (editingId && editNameRef.current) {
      editNameRef.current.focus()
      editNameRef.current.select()
    }
  }, [editingId])

  useEffect(() => {
    if (isCreatingNew && newNameRef.current) {
      newNameRef.current.focus()
    }
  }, [isCreatingNew])

  const loadFamilies = async () => {
    try {
      setLoading(true)
      const data = await fetchStrainFamilies(true)
      setFamilies(data)
    } catch (err) {
      console.error('Failed to load strain families:', err)
    } finally {
      setLoading(false)
    }
  }

  const notifyParent = () => {
    if (onDataChange) onDataChange()
  }

  const getSelectedLabel = () => {
    if (!value) return 'Select strain family...'
    const selected = families.find(f => f.id === value)
    return selected ? `${selected.name} (${selected.code})` : 'Select strain family...'
  }

  const handleSelect = (familyId) => {
    const family = families.find(f => f.id === familyId)
    onChange(familyId, family)
    setOpen(false)
  }

  const handleStartEdit = (family, e) => {
    e.stopPropagation()
    setEditingId(family.id)
    setEditingName(family.name)
    setEditingCode(family.code)
    setOriginalName(family.name)
    setOriginalCode(family.code)
  }

  const handleEditSave = async () => {
    if (!editingId) return

    const trimmedName = editingName.trim()
    const trimmedCode = editingCode.trim()

    if (trimmedName === '' && originalName !== '') {
      const count = await getProductCountByStrainFamily(editingId)
      setProductCount(count)
      setDeleteId(editingId)
      setShowDeleteDialog(true)
      return
    }

    if ((trimmedName !== originalName || trimmedCode !== originalCode) && trimmedName !== '' && trimmedCode !== '') {
      try {
        await updateStrainFamily(editingId, { name: trimmedName, code: trimmedCode })
        await loadFamilies()
        notifyParent()
      } catch (err) {
        console.error('Failed to update strain family:', err)
        alert(err.message || 'Failed to update strain family')
        setEditingName(originalName)
        setEditingCode(originalCode)
      }
    }

    setEditingId(null)
    setOriginalName('')
    setOriginalCode('')
  }

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleEditSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditingName(originalName)
      setEditingCode(originalCode)
      setEditingId(null)
      setOriginalName('')
      setOriginalCode('')
    }
  }

  const handleDeleteClick = async (familyId, e) => {
    e.stopPropagation()
    const count = await getProductCountByStrainFamily(familyId)
    setProductCount(count)
    setDeleteId(familyId)
    setShowDeleteDialog(true)
  }

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false)

    if (editingId === deleteId) {
      setEditingName(originalName)
      setEditingCode(originalCode)
      if (editNameRef.current) {
        setTimeout(() => editNameRef.current.focus(), 100)
      }
    }

    setDeleteId(null)
    setProductCount(0)
  }

  const handleDeleteProceed = async () => {
    try {
      await softDeleteStrainFamily(deleteId)
      await loadFamilies()
      notifyParent()

      if (value === deleteId) {
        onChange(null, null)
      }

      setShowDeleteDialog(false)
      setDeleteId(null)
      setProductCount(0)
      setEditingId(null)
      setOriginalName('')
      setOriginalCode('')
    } catch (err) {
      console.error('Failed to delete strain family:', err)
      alert(err.message || 'Failed to delete strain family')

      if (editingId === deleteId) {
        setEditingName(originalName)
        setEditingCode(originalCode)
      }

      setShowDeleteDialog(false)
    }
  }

  const handleAddNew = () => {
    setIsCreatingNew(true)
    setNewName('')
    setNewCode('')
  }

  const handleCreateNew = async () => {
    const trimmedName = newName.trim()
    const trimmedCode = newCode.trim()

    if (trimmedName === '') {
      alert('Strain family name cannot be empty')
      return
    }
    if (trimmedCode === '') {
      alert('Strain family code cannot be empty')
      return
    }

    try {
      const created = await createStrainFamily({
        name: trimmedName,
        code: trimmedCode,
        display_order: families.length + 1
      })

      await loadFamilies()
      notifyParent()

      onChange(created.id, created)
      setOpen(false)
      setIsCreatingNew(false)
      setNewName('')
      setNewCode('')
    } catch (err) {
      console.error('Failed to create strain family:', err)
      alert(err.message || 'Failed to create strain family')
    }
  }

  const handleNewKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCreateNew()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setIsCreatingNew(false)
      setNewName('')
      setNewCode('')
    }
  }

  const handleNewBlur = (e) => {
    // Don't cancel if focus moves to the other input in the create row
    const relatedTarget = e.relatedTarget
    if (relatedTarget && relatedTarget.dataset.createField) return
    setIsCreatingNew(false)
    setNewName('')
    setNewCode('')
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn("w-full justify-between bg-white hover:bg-gray-50 transition-colors", error && "border-red-500")}
          >
            <span className={cn(!value && "text-gray-500")}>
              {getSelectedLabel()}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search strain families..." />
            <CommandList>
              <CommandEmpty>No strain family found.</CommandEmpty>
              <CommandGroup>
                {loading ? (
                  <div className="py-6 text-center text-sm text-gray-500">
                    Loading...
                  </div>
                ) : (
                  <>
                    {families.map((family) => (
                      <CommandItem
                        key={family.id}
                        value={`${family.name} ${family.code}`}
                        onSelect={() => !editingId && handleSelect(family.id)}
                        onMouseEnter={() => setHoveredId(family.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        className="relative group"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === family.id ? "opacity-100" : "opacity-0"
                          )}
                        />

                        {editingId === family.id ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              ref={editNameRef}
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onBlur={handleEditSave}
                              onKeyDown={handleEditKeyDown}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Name"
                              className="flex-1 bg-white border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <input
                              type="text"
                              value={editingCode}
                              onChange={(e) => setEditingCode(e.target.value)}
                              onBlur={handleEditSave}
                              onKeyDown={handleEditKeyDown}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Code"
                              className="w-20 bg-white border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                        ) : (
                          <span className="flex-1">{family.name} ({family.code})</span>
                        )}

                        {hoveredId === family.id && editingId !== family.id && (
                          <div className="ml-auto flex items-center gap-1">
                            <button
                              onClick={(e) => handleStartEdit(family, e)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                              <Edit2 className="h-3 w-3 text-gray-600" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(family.id, e)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </button>
                          </div>
                        )}
                      </CommandItem>
                    ))}

                    {isCreatingNew ? (
                      <div className="px-2 py-1.5 flex items-center gap-2">
                        <Plus className="h-4 w-4 text-green-600 shrink-0" />
                        <input
                          ref={newNameRef}
                          type="text"
                          data-create-field="name"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          onBlur={handleNewBlur}
                          onKeyDown={handleNewKeyDown}
                          placeholder="Name"
                          className="flex-1 bg-white border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <input
                          type="text"
                          data-create-field="code"
                          value={newCode}
                          onChange={(e) => setNewCode(e.target.value)}
                          onBlur={handleNewBlur}
                          onKeyDown={handleNewKeyDown}
                          placeholder="Code"
                          className="w-20 bg-white border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    ) : (
                      <button
                        onClick={handleAddNew}
                        className="w-full px-2 py-1.5 flex items-center gap-2 text-green-600 hover:bg-gray-100 rounded-sm transition-colors text-sm"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add new strain family</span>
                      </button>
                    )}
                  </>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Strain Family?</AlertDialogTitle>
            <AlertDialogDescription>
              Warning: You are about to delete this strain family.
              {productCount > 0 ? (
                <span className="font-medium">
                  {' '}This will affect {productCount} product{productCount !== 1 ? 's' : ''}
                </span>
              ) : (
                <span> This will affect 0 products</span>
              )} and cannot be undone. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel} autoFocus>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProceed}
              variant="destructive"
            >
              Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
